// FTEDN Team — Feature Flags Admin Page
// Lists all flags grouped by mechanic, with live toggle/edit controls.

import { useState, useCallback } from 'react';
import {
  FLAG_DEFINITIONS,
  getFlagsByMechanic,
  type FlagName,
} from '@ftedn/shared';
import { useAllFlags } from '../../hooks/useFlag';
import { useApi } from '../../hooks/useApi';
import { FlagRow } from './FlagRow';
import './flags.css';

const MECHANIC_LABELS: Record<string, string> = {
  'mechanic.streaks': '🔥 Streaks',
  'mechanic.quests': '⚔️ Quests',
  'mechanic.leaderboard': '🏆 Leaderboard',
  'mechanic.referrals': '🔗 Referrals',
  'mechanic.openCocreation': '🌐 Open Co-creation',
  'mechanic.visibleImpact': '👁️ Visible Impact',
};

export function FlagAdminPage() {
  const flags = useAllFlags();
  const { post, del } = useApi();
  const [saving, setSaving] = useState<string | null>(null);
  const groups = getFlagsByMechanic();

  const handleUpdate = useCallback(async (name: FlagName, value: unknown) => {
    setSaving(name);
    try {
      await post('/flags/override', {
        scope: 'global',
        scopeId: 'active',
        flagName: name,
        value,
      });
    } catch (err) {
      console.error('[flags] Failed to save:', err);
    } finally {
      setSaving(null);
    }
  }, [post]);

  const handleReset = useCallback(async (name: FlagName) => {
    setSaving(name);
    try {
      await del(`/flags/override?scope=global&scopeId=active&flagName=${name}`);
    } catch (err) {
      console.error('[flags] Failed to reset:', err);
    } finally {
      setSaving(null);
    }
  }, [del]);

  return (
    <div className="mgmt-page flags-page">
      <div className="mgmt-header">
        <h1>🚩 Feature Flags</h1>
        <p className="mgmt-subtitle">
          Toggle mechanics, configure thresholds, and A/B test features in real time
        </p>
        <div className="mgmt-stats">
          <span className="stat-chip">
            {Object.keys(FLAG_DEFINITIONS).length} Flags
          </span>
          <span className="stat-chip approved">
            {Object.keys(groups).length} Mechanics
          </span>
        </div>
      </div>

      {Object.entries(groups).map(([mechanic, flagDefs]) => (
        <div key={mechanic} className="flag-group">
          <h2 className="flag-group-title">
            {MECHANIC_LABELS[mechanic] ?? mechanic}
          </h2>

          {flagDefs.map(({ name, def }) => {
            const defaultVal = def.default;
            const currentVal = flags[name] ?? defaultVal;
            const isOverridden = currentVal !== defaultVal;

            return (
              <FlagRow
                key={name}
                name={name}
                def={def}
                currentValue={currentVal}
                onUpdate={handleUpdate}
                onReset={handleReset}
                isOverridden={isOverridden || saving === name}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
